import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';
import { FloatingLabel } from './FloatingLabel';
import PropTypes from 'prop-types';
import Label from './Label';
import { Validator } from './ValidationResult';
import { makeLabel } from '../utils/generalUtils';
import { useAutofillNotifier } from '../hooks/autofillHook';
import { withComponentCapabilities } from './capabilities/componentCapabilities';
import { materializeValue } from '../utils/lazyValue';

const renderValue = (value, fixedDecimalPlaces) => {
    if (value === null || value === undefined) {
        return '';
    } else if (fixedDecimalPlaces) {
        const f = Number(value);
        return Number.isFinite(f) ? f.toFixed(fixedDecimalPlaces) : value.toString();
    } else {
        return value.toString()
    }
}

export const customCtrlDef = {
    toString: async ({ value, fixedDecimalPlaces, args, defaultValue, def }) => {
        const numericValue = Number.parseFloat(await materializeValue(value));
        if (Number.isNaN(numericValue)) {
            return defaultValue;
        }
        return renderValue(numericValue, fixedDecimalPlaces ?? args?.fixedDecimalPlaces ?? def.fixedDecimalPlaces);
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                [
                    { type: "number", field: "min", label: t('min') },
                    { type: "number", field: "max", label: t('max') },
                    { type: "number", field: "step", label: t('step', { count : 1 }) }
                ],
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, fixedDecimalPlaces, href, context: { args } = {}, ...props}) => {
        return <NumericInput min={def.min} max={def.max} step={def.step} fixedDecimalPlaces={fixedDecimalPlaces ?? args?.fixedDecimalPlaces ?? def.fixedDecimalPlaces} href={href ?? def.href} {...props}></NumericInput>        
    }
}

/**
 * Input textbox for recoding numbers - either integer or natural.
 * @component
 */
const NumericInput = withComponentCapabilities(({ id, value, live = false, onChange, placeholder, required = false, hideLabel = false, readonly = false, prependCtrl, prependText, appendCtrl, appendText, fixedDecimalPlaces, disabled = false, min, max, step, href, ...props}) => {
    const [text, setText] = useState(renderValue(value, fixedDecimalPlaces));
    const [validity, setValidity] = useState();
    const { createAutofillProps } = useAutofillNotifier();
    const ctrlRef = useRef();

    useEffect(() => {
        setValidity();
    }, [value]);

    useEffect(() => {
        if (!ctrlRef.current?.contains(document.activeElement)) {
            setText(renderValue(value, fixedDecimalPlaces));
        }
    }, [value, fixedDecimalPlaces]);

    const handleUpdate = useCallback((newText, isBlur) => {
        const newValue = newText === null || newText === undefined || newText === '' ? null : Number.parseFloat(newText);
        const valid = ctrlRef.current.checkValidity() && !Number.isNaN(newValue);
        setValidity(valid);
        if (valid && newValue !== (value ?? null)) {
            setText(renderValue(newValue, isBlur && fixedDecimalPlaces));
            onChange(Number.parseFloat(newValue));
        }
    }, [value, fixedDecimalPlaces, onChange]);

    const autofillProps = useMemo(() =>
        createAutofillProps((el) => {
            const newText = el.value;
            let shouldUpdate = false;

            setText(prev => {
                shouldUpdate = newText && prev !== newText;
                return newText;
            });

            if (shouldUpdate && (!required || newText)) {
                handleUpdate(newText);
                return true;
            }
            return shouldUpdate;
        }, ctrlRef)
    , [createAutofillProps, handleUpdate, required, value]);

    const handleChange = useCallback(e => {
        const newText = e.target.value;
        setText(newText);
        if (live || document.activeElement !== e.target) {
            handleUpdate(newText, false)
        }
    }, [handleUpdate, live]);

    if (prependCtrl && prependText) {
        throw new Error("Cannot specify both prependCtrl and prependText");
    }

    if (appendCtrl && appendText) {
        throw new Error("Cannot specify both appendCtrl and appendText");
    }

    let prepend = null;
    if (prependCtrl || prependText) {
        prepend = prependCtrl ?? <InputGroup.Text>{prependText}</InputGroup.Text>
    }

    let append = null;
    if (appendCtrl || appendText) {
        append = appendCtrl ?? <InputGroup.Text>{appendText}</InputGroup.Text>
    }

    const numericInput = <FormControl {...autofillProps} isInvalid={validity === false} type="number" value={text} onChange={e => handleChange(e)} onBlur={() => handleUpdate(text, true)} required={required} placeholder={makeLabel(placeholder, required)} disabled={disabled} min={min} max={max} step={step} onInvalid={() => setValidity(false)} {...props}></FormControl>;

    return readonly ? <Label id={id} placeholder={placeholder} hideLabel={hideLabel} text={text ?? ''} href={href}></Label> : <FloatingLabel id={id} hideLabel={hideLabel} placeholder={placeholder} required={required} value={text} href={href}>
        <Validator value={value} ref={ctrlRef} onValidation={setValidity}>
        { prepend || append ? 
            <InputGroup>
                {prepend? <InputGroup.Prepend>{prepend}</InputGroup.Prepend> : null}
                {numericInput}
                {append? <InputGroup.Append>{append}</InputGroup.Append> :  null}
            </InputGroup>
            : numericInput
        }
        </Validator>
    </FloatingLabel>
}, {

});

NumericInput.propTypes = {
    /**
     * The number to be displayed by the control
     */
    value: PropTypes.number,
    /**
     * Set to true for every key stroke to call the onChange function
     */
    live: PropTypes.bool,
    /**
     * Function to call when the input value has been changed
     */
    onChange: PropTypes.func,
    /**
     * The label for the control
     */
    placeholder: PropTypes.string,
    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,
    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,
    /**
     * Render the control value as text rather an input control
     */
    readonly: PropTypes.bool,
    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,
    /**
     * The minimum value that can be entered
     */
    min: PropTypes.number,
    /**
     * The maximum value that can be entered
     */
    max: PropTypes.number,
    /**
     * The amount to increase or decrease value by when applying the up/down arrows on the
     * control / the precision of the numeric value.
     */
    step: PropTypes.number,

    /**
     * A control to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependCtrl: PropTypes.node,

    /**
     * Text or icon to prepend to the beginning of the text input. It is invalid to specify both prependCtrl and prependText
     */
    prependText: PropTypes.node,

    /**
     * A control to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
     */
    appendCtrl: PropTypes.node,

    /**
    * Text or icon to append to the end of the text input. It is invalid to specify both appendCtrl and appendText
    */
    appendText: PropTypes.node,

    /**
     * Force the number to always have a fixed number of decimal places and always render them even when zero
     */
    fixedDecimalPlaces: PropTypes.number,
    
    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}
export default NumericInput;